/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseFinalWeightPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/final-weight 最終重量（S7 spec §8.2 / §11.2 畫面 11）
 *                朝日出貨重量 + 落札業者入荷重量 + 最終採用重量,逐段呈現並計算差異;
 *                最終 vs 前段 > 20kg → highlight + 提示重量稽核 / 報連相(同 S3 模式)。
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  資料來源（真相,執行中 binary 已驗 2026-06-01）：
 *    - workflow-state：權威狀態（驅動「最終重量受領」推進按鈕的可達性）。
 *    - shipment.receiving_details：逐品「朝日出貨重量」（received_weight_kg,会場過磅）
 *      與「メンバー申告重量」（original_declared_weight_kg）+ 後端已算 variance_kg /
 *      variance_over_threshold（後端權威）。
 *    - weight-audits：既有重量差異 review（> 20kg 時建立的現場報連相）。
 *    - GET /cases/:id/final-weight 為 v12-keyed,對 v13 案件回 404 → null（不阻擋）。
 *  20kg 規則（spec §D / §12.3）：最終採用重量 vs 前段(会場)abs 差異 > 20kg 時 highlight,
 *    並提示需建立 weight_variance review / 報連相;前端不自動裁定權威重量。
 *  spine 推進：POST /cases/:id/final-weight/receive（P2PaymentConfirmed → FinalWeightReceived）。
 *  RBAC：寫入（受領推進）= operations.weighings.record（PERMISSIONS.finalWeightManage）。
 *  ⚠️ 重量一律字串型 Decimal,前端不得轉 float 做累積（CLAUDE.md §2）;
 *     此處 Number() 僅供 UI 差異提示,權威差異看後端 variance_kg。
 */

import { useMemo, type ReactElement } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getCaseShipment, type ReceivingItemDetailDto } from '../api/shipments'
import { listWeightVarianceReviews } from '../api/weights'
import { getWorkflowState, receiveFinalWeight } from '../api/workflow'
import { getFinalWeight } from '../api/reports'
import { ApiError } from '../api/client'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { cn } from '../lib/cn'
import { toast } from '../components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** spec §D 20kg 閾值（前端輸入階段提示用;權威判定仍以後端 variance_kg 為準）。 */
const FINAL_WEIGHT_THRESHOLD_KG = 20

/** 觸發「最終重量受領」推進的前置狀態（workflow allowed_next 含 FinalWeightReceived）。 */
const FINAL_WEIGHT_NEXT = 'FinalWeightReceived'

function fmtKg(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  return Number.isFinite(num) ? num.toLocaleString('ja-JP', { maximumFractionDigits: 3 }) : value
}

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

/** abs 差異 kg（前段会場重量 vs メンバー申告;僅供 UI 提示）。 */
function absVariance(detail: ReceivingItemDetailDto): number {
  const member = Number(detail.original_declared_weight_kg)
  const venue = Number(detail.received_weight_kg)
  if (!Number.isFinite(member) || !Number.isFinite(venue)) return Number.NaN
  return Math.abs(member - venue)
}

/** 是否最終 vs 前段差異超過 20kg（後端 variance_over_threshold 仍為權威）。 */
function isOverThreshold(detail: ReceivingItemDetailDto): boolean {
  if (detail.variance_over_threshold) return true
  const abs = absVariance(detail)
  return Number.isFinite(abs) && abs > FINAL_WEIGHT_THRESHOLD_KG
}

export function CaseFinalWeightPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canRecord = can(PERMISSIONS.finalWeightManage)

  const workflowQuery = useQuery({
    queryKey: ['case', caseId, 'workflow-state'],
    queryFn: () => getWorkflowState(caseId),
    enabled: caseId !== '',
  })
  const shipmentQuery = useQuery({
    queryKey: ['case', caseId, 'shipment'],
    queryFn: () => getCaseShipment(caseId),
    enabled: caseId !== '',
    retry: false,
  })
  const reviewsQuery = useQuery({
    queryKey: ['case', caseId, 'weight-audits'],
    queryFn: () => listWeightVarianceReviews(caseId),
    enabled: caseId !== '',
  })
  // 最終重量 snapshot（v13 目前回 null;保留以便日後 v13 補出 snapshot）。
  const finalWeightQuery = useQuery({
    queryKey: ['case', caseId, 'final-weight'],
    queryFn: () => getFinalWeight(caseId),
    enabled: caseId !== '',
    retry: false,
  })

  const receiveMutation = useMutation({
    mutationFn: () => receiveFinalWeight(caseId),
    onSuccess: (result) => {
      toast.success(t('finalWeight.receive.success'))
      queryClient.setQueryData(['case', caseId, 'workflow-state'], (prev: unknown) =>
        prev && typeof prev === 'object'
          ? { ...prev, workflow_state: result.workflow_state }
          : prev,
      )
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'workflow-state'] })
    },
    onError: (error) => toast.error(`${t('finalWeight.receive.error')}${errDetail(error)}`),
  })

  const details = useMemo<ReceivingItemDetailDto[]>(
    () => shipmentQuery.data?.receiving_details ?? [],
    [shipmentQuery.data],
  )
  const reviews = reviewsQuery.data?.data ?? []
  const overItems = details.filter((d) => isOverThreshold(d))
  const hasFinalWeightSnapshot = finalWeightQuery.data != null

  const workflowState = workflowQuery.data?.workflow_state ?? null
  const allowedNext = workflowQuery.data?.allowed_next ?? []
  const isLocked = workflowQuery.data?.is_blocking === true && allowedNext.length === 0
  const canAdvanceReceive = allowedNext.includes(FINAL_WEIGHT_NEXT)

  if (workflowQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <header className="page-heading">
        <span className="eyebrow">{t('case.stage.finalWeight')}</span>
        <h2 className="text-lg font-semibold">{t('finalWeight.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('finalWeight.subtitle')}</p>
      </header>

      {/* workflow 狀態 + 受領推進 */}
      <Card>
        <CardHeader>
          <CardTitle>{t('finalWeight.state.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('finalWeight.state.current')}</span>
            <Badge variant={isLocked ? 'outline' : 'secondary'}>{workflowState ?? '—'}</Badge>
            {isLocked ? <Badge variant="outline">{t('finalReport.locked.badge')}</Badge> : null}
          </div>
          {isLocked ? (
            <p className="text-sm text-muted-foreground">{t('finalWeight.state.lockedHint')}</p>
          ) : canAdvanceReceive ? (
            canRecord ? (
              <div>
                <Button
                  type="button"
                  data-testid="final-weight-receive"
                  disabled={receiveMutation.isPending || overItems.length > 0}
                  onClick={() => receiveMutation.mutate()}
                >
                  {receiveMutation.isPending
                    ? t('finalWeight.receive.running')
                    : t('finalWeight.receive.button')}
                </Button>
                {overItems.length > 0 ? (
                  <p className="mt-2 text-sm text-destructive">
                    {t('finalWeight.receive.blockedByVariance')}
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('finalWeight.receive.hint')}
                  </p>
                )}
              </div>
            ) : (
              <Badge variant="outline">{t('rbac.forbidden')}</Badge>
            )
          ) : (
            <p className="text-sm text-muted-foreground">{t('finalWeight.state.notReady')}</p>
          )}
        </CardContent>
      </Card>

      {/* 逐段重量對照（朝日出貨 / 落札業者入荷 / 最終採用）*/}
      <Card>
        <CardHeader>
          <CardTitle>{t('finalWeight.compare.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {shipmentQuery.isPending ? (
            <Skeleton className="h-32 w-full" />
          ) : shipmentQuery.isError ? (
            <p className="text-sm text-muted-foreground">
              {t('finalWeight.compare.unavailable')}
              {errDetail(shipmentQuery.error)}
            </p>
          ) : details.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('finalWeight.compare.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('field.itemName')}</TableHead>
                    <TableHead className="text-right">{t('finalWeight.col.memberWeight')}</TableHead>
                    <TableHead className="text-right">{t('finalWeight.col.venueWeight')}</TableHead>
                    <TableHead className="text-right">{t('finalWeight.col.finalWeight')}</TableHead>
                    <TableHead className="text-right">{t('finalWeight.col.variance')}</TableHead>
                    <TableHead>{t('finalWeight.col.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {details.map((detail) => {
                    const over = isOverThreshold(detail)
                    return (
                      <TableRow key={detail.id} className={cn(over && 'bg-destructive/10')}>
                        <TableCell className="font-medium">{detail.received_item_name}</TableCell>
                        <TableCell className="text-right">
                          {fmtKg(detail.original_declared_weight_kg)}
                        </TableCell>
                        <TableCell className="text-right">
                          {fmtKg(detail.received_weight_kg)}
                        </TableCell>
                        {/* 最終採用重量：v13 尚未獨立記錄,沿用会場荷受け重量為目前採用值 */}
                        <TableCell className="text-right">
                          {fmtKg(detail.received_weight_kg)}
                        </TableCell>
                        <TableCell
                          className={cn('text-right', over && 'font-semibold text-destructive')}
                        >
                          {fmtKg(detail.variance_kg)}
                        </TableCell>
                        <TableCell>
                          {over ? (
                            <Badge variant="destructive">
                              {t('finalWeight.status.overThreshold')}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">{t('finalWeight.status.ok')}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {overItems.length > 0 ? (
            <p className="text-sm text-destructive">
              {t('finalWeight.overThresholdHint').replace(
                '{kg}',
                String(
                  Math.round(
                    Math.max(
                      ...overItems.map((d) => absVariance(d)).filter((n) => Number.isFinite(n)),
                    ),
                  ),
                ),
              )}
            </p>
          ) : null}
          {!hasFinalWeightSnapshot ? (
            <p className="text-xs text-muted-foreground">{t('finalWeight.snapshot.none')}</p>
          ) : null}
        </CardContent>
      </Card>

      {/* 相關重量差異 review（現場報連相）*/}
      <Card>
        <CardHeader>
          <CardTitle>
            {t('finalWeight.reviews.title')}（{reviews.length}）
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reviewsQuery.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('finalWeight.reviews.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('weightAudit.col.varianceType')}</TableHead>
                    <TableHead className="text-right">{t('weightAudit.col.variance')}</TableHead>
                    <TableHead>{t('weightAudit.col.status')}</TableHead>
                    <TableHead>{t('weightAudit.col.resolution')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.map((review) => (
                    <TableRow key={review.id}>
                      <TableCell>{review.variance_type}</TableCell>
                      <TableCell className="text-right">{fmtKg(review.variance_kg)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{review.status}</Badge>
                      </TableCell>
                      <TableCell>{review.resolution_type ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
